import { yupResolver } from '@hookform/resolvers/yup'
import { findIndex, isEqual, map } from 'lodash-es'
import { ChangeEvent, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'

import { AlertRatio } from '@/components/AlertRatio'
import { PageTitle } from '@/components'
import { Button, Input, Modal, Textarea } from '@/components/ui'
import { Languages, MemoryCustomCover } from '../../../common/models'
import { MemoryCustomCoverTranslations } from '../../../common/models/memories'
import FormGroup from '../../../core/components/FormGroup'
import { FormUploadPreview } from '../../../core/components/FormUploadPreview'
import {
  getMemoryCustomCover,
  updateMemoryCustomCover,
} from '../../../core/services/memories'
import { uploadImage } from '../../../core/services/upload-image'
import { MessageBox } from '../messages/MessageBox'

export interface ICustomCoverValues {
  translations: MemoryCustomCoverTranslations[]
  imageLink: string
  logoLink: string
}

export interface MemoriesCreateModalProps {
  setShowCreateModal: (showEdit: boolean) => void
  showCreateModal: boolean
  allLanguages: Languages[]
}

export const MemoriesCustomCoverModal = (
  props: MemoriesCreateModalProps,
): JSX.Element => {
  const { setShowCreateModal, showCreateModal, allLanguages } = props
  const [image, setImage] = useState('')
  const [imageName, setImageName] = useState('')
  const [logo, setLogo] = useState('')
  const [logoName, setLogoName] = useState('')
  const [invalidPhoto, setInvalidPhoto] = useState<boolean>(false)
  const [invalidPhotoLogo, setInvalidPhotoLogo] = useState<boolean>(false)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [content, setContent] = useState<MemoryCustomCoverTranslations[]>([])
  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [isErrorRatio, setIsErrorRatio] = useState<boolean>(false)
  const [isErrorRatioLogo, setIsErrorRatioLogo] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        const temp = map(allLanguages, lang => {
          return {
            locale: lang.locale,
            description: '',
            name: '',
          }
        })
        setValue('translations', temp)
        if (showCreateModal) {
          const response = await getMemoryCustomCover()
          if (!!response.data.translations && response.data.translations.length > 0) {
            setTimeout(() => {
              map(response.data.translations, trans => {
                const index = findIndex(allLanguages, l => l.locale === trans.locale)
                if (index !== -1) {
                  setValue(`translations.${index}.locale`, trans.locale)
                  setValue(`translations.${index}.description`, trans.description)
                  setValue(`translations.${index}.name`, trans.name)
                }
              })
            }, 100)
          }
          setContent(response.data.translations)
          setValue('imageLink', response.data.imageLink)
          setValue('logoLink', response.data.introLogoMarkLink)
          setImage(response.data.imageLink)
          if (response.data.imageLink) {
            const url = new URL(response.data.imageLink)
            const i = url?.pathname?.lastIndexOf('/') + 1
            const imageName = url?.pathname?.substring(i)
            setImageName(imageName)
          }
          setLogo(response.data.introLogoMarkLink)
          if (response.data.introLogoMarkLink) {
            const logoUrl = new URL(response.data.introLogoMarkLink)
            const j = logoUrl?.pathname?.lastIndexOf('/') + 1
            const logoName = logoUrl?.pathname?.substring(j)
            setLogoName(logoName)
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    }
    )()
    return () => {
      setImage('')
      setImageName('')
      setLogo('')
      setLogoName('')
      reset()
    }
  }, [showCreateModal, allLanguages])

  const schema = yup.object().shape({
    imageLink: yup.string().required(),
    logoLink: yup.string().required(),
    translations: yup
      .array()
      .min(1)
      .of(
        yup.object().shape({
          locale: yup.string().nullable().required('Field is required'),
          name: yup.string().required(),
          description: yup.string().required(),
        }),
      )
      .required(),
  })

  const {
    handleSubmit,
    register,
    formState: { errors, dirtyFields },
    reset,
    watch,
    setValue,
  } = useForm<ICustomCoverValues>({
    mode: 'onChange',
    resolver: yupResolver<ICustomCoverValues>(schema),
  })

  const handleClose = () => {
    setInvalidPhoto(false)
    setInvalidPhotoLogo(false)
    setShowCreateModal(false)
    setImage('')
    setImageName('')
    reset()
    setIsErrorRatio(false)
    setIsErrorRatioLogo(false)
  }

  const submit = (values: ICustomCoverValues) => {
    // prettier-ignore
    (async () => {
      setIsSubmitting(true)
      try {
        const data: MemoryCustomCover = {
          translations: values.translations,
          imageLink: values.imageLink,
          introLogoMarkLink: values.logoLink,
        }
        const { status } = await updateMemoryCustomCover(data)
        if (status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setIsSubmitting(false)
      }
      handleClose()
    })()
  }

  const contentWatch = watch('translations')

  return (
    <>
      <Modal
        open={showCreateModal}
        onOpenChange={(open) => {
          if (!open) handleClose()
        }}
        size="lg"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle title="Memories" subtitle="Customise Cover Image" />
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-end gap-2">
            <Button onClick={handleClose} type="button" variant="secondary">
              Cancel
            </Button>
            <Button
              disabled={
                isSubmitting ||
                isUploading ||
                (Object.keys(dirtyFields).length === 0 &&
                  isEqual(contentWatch, content)) ||
                isErrorRatio ||
                isErrorRatioLogo
              }
              form="custom-cover-form"
              type="submit"
              variant="primary"
              loading={isSubmitting || isUploading}
            >
              Save
            </Button>
          </div>
        }
      >
        <form
          id="custom-cover-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-7"
        >
          <div className="flex w-full flex-col gap-2">
            {map(allLanguages, (language, index) => {
              return (
                <div
                  className="flex w-full flex-col gap-3 rounded-[10px] border border-monochrome-120 bg-monochrome-5-50 p-4"
                  key={language.id}
                >
                  <FormGroup label="Language:" wrapperClassName="mb-0">
                    <span className="text-sm font-semibold text-accent-1">
                      {language.name}
                    </span>
                  </FormGroup>
                  <FormGroup
                    label="Title"
                    error={errors?.translations?.[index]?.name?.message}
                  >
                    <Input
                      isInvalid={!!errors?.translations?.[index]?.name?.message}
                      {...register(`translations.${index}.name`)}
                      type="text"
                      placeholder="Enter Name"
                    />
                  </FormGroup>
                  <FormGroup
                    label="Description"
                    error={errors?.translations?.[index]?.description?.message}
                  >
                    <Textarea
                      isInvalid={
                        !!errors?.translations?.[index]?.description?.message
                      }
                      {...register(`translations.${index}.description`)}
                      rows={5}
                      placeholder="Enter Description"
                    />
                  </FormGroup>
                </div>
              )
            })}
          </div>
          <FormUploadPreview
            control={
              <Input
                multiple={false}
                accept="image/*"
                isInvalid={
                  !!errors?.imageLink?.message || invalidPhoto || isErrorRatio
                }
                name="photo"
                onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                  const file = e?.target?.files?.[0]
                  if (file) {
                    setInvalidPhoto(false)
                    setIsErrorRatio(false)
                    if (file.size > 1_000_000) {
                      setInvalidPhoto(true)
                      setIsError(true)
                      setShowMessageBox(true)
                      setMessage('The photo is too large (1 MB)')
                      return
                    }
                    const data = await uploadImage(
                      file,
                      setIsUploading,
                      setIsError,
                      setShowMessageBox,
                      setMessage,
                      setIsErrorRatio,
                      'MEMORY_COVER',
                    )
                    if (data.photoUrl) {
                      setImage(data.photoUrl)
                      setImageName(file.name)
                      setValue('imageLink', data.photoUrl, {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                  }
                }}
                type="file"
                className="bg-monochrome-5-50"
              />
            }
            imageName={imageName}
            isUploading={isUploading}
            url={image}
            onRemove={() => {
              setImage('')
              setImageName('')
              setValue('imageLink', '')
            }}
            imageStyle={{
              objectFit: 'contain',
            }}
            label="Image (630X360)"
            previewLabel="Image (630X360)"
          />
          {isErrorRatio && <AlertRatio />}
          <FormUploadPreview
            control={
              <Input
                multiple={false}
                accept="image/*"
                isInvalid={
                  !!errors?.logoLink?.message ||
                  invalidPhotoLogo ||
                  isErrorRatioLogo
                }
                name="photo"
                onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                  const file = e?.target?.files?.[0]
                  if (file) {
                    setInvalidPhotoLogo(false)
                    setIsErrorRatioLogo(false)
                    if (file.size > 1_000_000) {
                      setInvalidPhotoLogo(true)
                      setIsError(true)
                      setShowMessageBox(true)
                      setMessage('The photo is too large (1 MB)')
                      return
                    }
                    const data = await uploadImage(
                      file,
                      setIsUploading,
                      setIsError,
                      setShowMessageBox,
                      setMessage,
                      setIsErrorRatioLogo,
                      'MEMORY_COVER_INTRO_LOGO_MARK',
                    )
                    if (data.photoUrl) {
                      setLogo(data.photoUrl)
                      setLogoName(file.name)
                      setValue('logoLink', data.photoUrl, {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                  }
                }}
                type="file"
                className="bg-monochrome-5-50"
              />
            }
            imageName={logoName}
            isUploading={isUploading}
            url={logo}
            onRemove={() => {
              setLogo('')
              setLogoName('')
              setValue('logoLink', '')
            }}
            imageStyle={{
              objectFit: 'contain',
            }}
            label="Intro Logo Mark (50X50)"
            previewLabel="Intro Logo Mark (50X50)"
          />
          {isErrorRatioLogo && <AlertRatio />}
        </form>
      </Modal>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
